import { ensureSearchSalt, toBase64 } from './crypto.ts';

/**
 * Token pencarian offline (T-P5-13, FR-SEARCH-05).
 *
 * Nama dan telepon nasabah tersimpan sebagai ciphertext, sehingga pencarian
 * tidak bisa memindai isinya. Sebagai gantinya tiap nilai dipecah menjadi
 * potongan dan di-HMAC dengan salt perangkat; pencarian mengubah kata kunci
 * dengan cara yang sama lalu mencocokkan token. Yang tersimpan tetap tidak
 * terbaca, tetapi indeks multi-entry Dexie tetap dapat dipakai.
 *
 * Batasnya jelas dan disengaja: hanya cocok dari **awal kata** (prefix). Substring
 * di tengah kata memerlukan indeks n-gram yang membocorkan jauh lebih banyak
 * tentang isi aslinya.
 */

const MIN_PREFIX = 3;
const MAX_PREFIX = 12;

let cachedKey: CryptoKey | null = null;

async function hmacKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const salt = await ensureSearchSalt();
  cachedKey = await globalThis.crypto.subtle.importKey(
    'raw',
    salt as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );

  return cachedKey;
}

/** Dipanggil setelah wipe: salt berganti, token lama tidak boleh dipakai lagi. */
export function resetSearchKey(): void {
  cachedKey = null;
}

export function normalizeTerm(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

async function hash(value: string): Promise<string> {
  const signature = await globalThis.crypto.subtle.sign(
    'HMAC',
    await hmacKey(),
    new TextEncoder().encode(value),
  );

  // 12 byte pertama sudah cukup: ruangnya 2^96, sementara satu perangkat hanya
  // menyimpan puluhan ribu token — tabrakan tidak realistis dan indeksnya kecil.
  return toBase64(new Uint8Array(signature).subarray(0, 12));
}

/** Token untuk satu nilai yang disimpan (nama, telepon, nomor pinjaman). */
export async function buildSearchTokens(values: (string | null | undefined)[]): Promise<string[]> {
  const prefixes = new Set<string>();

  for (const value of values) {
    if (!value) continue;

    for (const word of normalizeTerm(value).split(' ')) {
      if (word.length === 0) continue;

      // Nomor telepon dan nomor pinjaman juga dicari dari ekornya; 4 digit
      // terakhir adalah cara collector menyebut keduanya di lapangan.
      if (/^\d{4,}$/.test(word)) prefixes.add(word.slice(-4));

      const limit = Math.min(word.length, MAX_PREFIX);
      for (let length = Math.min(MIN_PREFIX, word.length); length <= limit; length += 1) {
        prefixes.add(word.slice(0, length));
      }
    }
  }

  return Promise.all([...prefixes].map(hash));
}

/** Token dari kata kunci pencarian; dicocokkan dengan `anyOf` pada indeks Dexie. */
export async function buildQueryTokens(query: string): Promise<string[]> {
  const words = normalizeTerm(query)
    .split(' ')
    .filter((word) => word.length >= Math.min(MIN_PREFIX, query.trim().length));

  return Promise.all(words.map((word) => hash(word.slice(0, MAX_PREFIX))));
}
